import { faKey } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { yupResolver } from '@hookform/resolvers/yup';
import { AlertBanner, AlertBannerStatus, SwitchThemeContext, TextField } from '@tryhackme/thm-ui-components';
import { useContext, useEffect, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PasswordInput } from 'src/common/components';
import { GOOGLE_SITE_KEY, LOGIN_REFERRER } from 'src/common/constants';
import { useForm } from 'src/common/form';
import { useFeatureIsOn } from 'src/common/growthbook';
import { isQueryErrorWithMessage } from 'src/common/helpers/slices';
import { useSocialLoginUrl } from 'src/common/hooks/use-social-login-url';
import type { LoginResponse } from 'src/common/interfaces';
import { apiSlice } from 'src/features/api/api-slice';
import { getLoginReferrer } from 'src/features/login/login.constants';
import { StyledFormSubtitle, StyledFormTitle } from 'src/features/login/login.styles';
import { SocialSignupType } from 'src/features/signup/components/signup-form/signup-form.types';
import * as yup from 'yup';
import { useLoginMutation } from './login-form.slice';
import { StyledForgotPasswordButton, StyledForm, StyledFormControl, StyledFullWidthButton, StyledFullWidthButtonWrapper, StyledFullWidthLink, StyledGoogleLogo, StyledHorizontalRule, StyledLinkedInLogo, StyledLoginButtonAndLinkContainer, StyledLoginForm, StyledOrContainer, StyledOrLabel, StyledRegisterLink, StyledTextWithLink } from './login-form.styles';
import type { LoginFormValues } from './login-form.types';
const reCaptchaBypass = process.env.REACT_APP_RECAPTCHA_BYPASS === 'true';
export const LoginForm = () => {
  const navigate = useNavigate();
  const referrer = getLoginReferrer();
  const [searchParams] = useSearchParams();
  const googleLoginUrl = useSocialLoginUrl({
    redirectTo: referrer,
    social: SocialSignupType.GOOGLE
  });
  const linkedInLoginUrl = useSocialLoginUrl({
    redirectTo: referrer,
    social: SocialSignupType.LINKEDIN
  });
  const {
    theme
  } = useContext(SwitchThemeContext);
  const reCaptchaRef = useRef<ReCAPTCHA>(null);
  const [performLogin, {
    isLoading
  }] = useLoginMutation();
  const [loginError, setLoginError] = useState<string | null>(null);
  const isAdminLoginRedirectEnabled = useFeatureIsOn('b2b-admin-login-redirect');
  const isLinkedInLoginEnabled = useFeatureIsOn('linkedin-social-login');
  const {
    handleSubmit,
    formState: {
      errors
    },
    register,
    reset
  } = useForm<LoginFormValues>({
    resolver: yupResolver(yup.object().shape({
      usernameOrEmail: yup.string().required('Username or email is required'),
      password: yup.string().required('Password is required')
    }))
  });
  const {
    usernameOrEmail: usernameError,
    password: passwordError
  } = errors;

  // Check for Google OAuth error in URL parameters
  useEffect(() => {
    const errorParam = searchParams.get('error');
    if (errorParam) {
      setLoginError(decodeURIComponent(errorParam));
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('error');
      navigate({
        search: newSearchParams.toString()
      }, {
        replace: true
      });
    }
  }, [searchParams, navigate]);
  const navigateToDashboard = (result: LoginResponse, to: string, condition: boolean) => {
    // Check if user is a company admin and redirect to company dashboard
    if (isAdminLoginRedirectEnabled && result?.isB2BAdmin && condition) {
      const companyId = result.companyId;
      navigate(`/client/${companyId}/dashboard`);
    } else {
      navigate(to);
    }
  };
  const handleOnSuccess = (data: LoginFormValues) => {
    setLoginError(null);
    const reCaptcha = reCaptchaRef.current?.getValue();
    if (reCaptcha || reCaptchaBypass) {
      performLogin({
        reCaptcha,
        password: data.password,
        email: data.usernameOrEmail
      }).unwrap().then((result: LoginResponse) => {
        window.sessionStorage.removeItem(LOGIN_REFERRER);
        if (result && result.status === 'error') {
          reCaptchaRef.current?.reset();
          setLoginError(result.message ?? 'An error has occurred, please try again');
          reset();
        } else if (referrer) {
          apiSlice.util.resetApiState();
          navigateToDashboard(result, referrer, referrer === '/');
        } else {
          apiSlice.util.resetApiState();
          navigateToDashboard(result, '/dashboard', true);
        }
      }).catch((error: unknown) => {
        const errorMessage = isQueryErrorWithMessage(error) ? error.data.message : 'An error has occurred, please try again';
        reCaptchaRef.current?.reset();
        setLoginError(errorMessage ?? 'An error has occurred, please try again');
        reset();
      });
    }
  };
  return <StyledLoginForm data-sentry-element="StyledLoginForm" data-sentry-component="LoginForm" data-sentry-source-file="login-form.tsx">
      {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
      <StyledForm onSubmit={handleSubmit(handleOnSuccess)} data-sentry-element="StyledForm" data-sentry-source-file="login-form.tsx">
        <header>
          <StyledFormTitle data-sentry-element="StyledFormTitle" data-sentry-source-file="login-form.tsx">Welcome back!</StyledFormTitle>
          <StyledFormSubtitle data-sentry-element="StyledFormSubtitle" data-sentry-source-file="login-form.tsx">Log in to your account.</StyledFormSubtitle>
        </header>
        {loginError && <AlertBanner status={AlertBannerStatus.ERROR} message={loginError} />}
        <StyledFormControl data-sentry-element="StyledFormControl" data-sentry-source-file="login-form.tsx">
          <TextField {...register('usernameOrEmail')} autoComplete="on" autoFocus id="username-or-email-field" label="Username or email" placeholder="example@example.com" type="text" variant="input" isError={!!usernameError?.message} errorMessage={usernameError?.message as string} data-sentry-element="TextField" data-sentry-source-file="login-form.tsx" />
        </StyledFormControl>
        <StyledFormControl data-sentry-element="StyledFormControl" data-sentry-source-file="login-form.tsx">
          <PasswordInput {...register('password')} autoComplete="current-password" id="password-field" label="Password" placeholder="Password" isError={!!passwordError?.message} errorMessage={passwordError?.message as string} data-sentry-element="PasswordInput" data-sentry-source-file="login-form.tsx" />
          <StyledForgotPasswordButton to="/forgot" data-sentry-element="StyledForgotPasswordButton" data-sentry-source-file="login-form.tsx">Forgot your password?</StyledForgotPasswordButton>
        </StyledFormControl>
        <ReCAPTCHA sitekey={GOOGLE_SITE_KEY} ref={reCaptchaRef} data-testid="recaptcha-comp" theme={theme === 'dark' ? 'dark' : 'light'} style={{
        width: '100%',
        margin: '0'
      }} data-sentry-element="ReCAPTCHA" data-sentry-source-file="login-form.tsx" />
        <StyledLoginButtonAndLinkContainer data-sentry-element="StyledLoginButtonAndLinkContainer" data-sentry-source-file="login-form.tsx">
          <StyledFullWidthButton type="submit" isLoading={isLoading} isDisabled={isLoading} data-sentry-element="StyledFullWidthButton" data-sentry-source-file="login-form.tsx">
            Log in
          </StyledFullWidthButton>
          <StyledTextWithLink data-sentry-element="StyledTextWithLink" data-sentry-source-file="login-form.tsx">
            Having trouble logging in? <StyledRegisterLink to="/login/magic" data-sentry-element="StyledRegisterLink" data-sentry-source-file="login-form.tsx">Send magic link</StyledRegisterLink>
          </StyledTextWithLink>
        </StyledLoginButtonAndLinkContainer>
      </StyledForm>
      <StyledOrContainer data-sentry-element="StyledOrContainer" data-sentry-source-file="login-form.tsx">
        <StyledHorizontalRule data-sentry-element="StyledHorizontalRule" data-sentry-source-file="login-form.tsx" />
        <StyledOrLabel data-sentry-element="StyledOrLabel" data-sentry-source-file="login-form.tsx">Or</StyledOrLabel>
        <StyledHorizontalRule data-sentry-element="StyledHorizontalRule" data-sentry-source-file="login-form.tsx" />
      </StyledOrContainer>
      <StyledFullWidthButtonWrapper data-sentry-element="StyledFullWidthButtonWrapper" data-sentry-source-file="login-form.tsx">
        <StyledFullWidthLink to={googleLoginUrl} data-sentry-element="StyledFullWidthLink" data-sentry-source-file="login-form.tsx">
          <StyledFullWidthButton type="button" variant="outlined" data-sentry-element="StyledFullWidthButton" data-sentry-source-file="login-form.tsx">
            <StyledGoogleLogo data-sentry-element="StyledGoogleLogo" data-sentry-source-file="login-form.tsx" />
            <span>Continue with Google</span>
          </StyledFullWidthButton>
        </StyledFullWidthLink>
        {isLinkedInLoginEnabled && <StyledFullWidthLink to={linkedInLoginUrl}>
            <StyledFullWidthButton type="button" variant="outlined">
              <StyledLinkedInLogo />
              <span>Continue with LinkedIn</span>
            </StyledFullWidthButton>
          </StyledFullWidthLink>}
        <StyledFullWidthLink to="/login/sso" data-sentry-element="StyledFullWidthLink" data-sentry-source-file="login-form.tsx">
          <StyledFullWidthButton type="button" variant="outlined" data-sentry-element="StyledFullWidthButton" data-sentry-source-file="login-form.tsx">
            <FontAwesomeIcon icon={faKey} data-sentry-element="FontAwesomeIcon" data-sentry-source-file="login-form.tsx" />
            <span>Continue with SSO</span>
          </StyledFullWidthButton>
        </StyledFullWidthLink>
      </StyledFullWidthButtonWrapper>
      <StyledTextWithLink data-sentry-element="StyledTextWithLink" data-sentry-source-file="login-form.tsx">
        Don&apos;t have an account? <StyledRegisterLink to="/signup" data-sentry-element="StyledRegisterLink" data-sentry-source-file="login-form.tsx">Sign up</StyledRegisterLink>.
      </StyledTextWithLink>
    </StyledLoginForm>;
};